import { useState, useEffect } from 'react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';
import { Loader2, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AGBPage = () => {
  const [loading, setLoading] = useState(true);
  const [agb, setAgb] = useState(null);

  useEffect(() => {
    fetchAGB();
  }, []);

  const fetchAGB = async () => {
    try {
      const response = await fetch(`${API}/api/terms`);
      if (response.ok) {
        const data = await response.json();
        setAgb(data);
      }
    } catch (error) {
      console.error('Error fetching AGB:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isHtml = (text) => {
    if (!text) return false;
    return /<[a-z][\s\S]*>/i.test(text);
  };

  const renderContent = (text) => {
    if (!text) return null;
    if (isHtml(text)) {
      return <div className="agb-content" dangerouslySetInnerHTML={{ __html: text }} />;
    }
    const paragraphs = text.split(/\n\n+/);
    return paragraphs.map((para, idx) => {
      const lines = para.split('\n');
      return (
        <p key={idx} className="mb-4 leading-relaxed">
          {lines.map((line, lineIdx) => (
            <span key={lineIdx}>{line}{lineIdx < lines.length - 1 && <br />}</span>
          ))}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <article className="prose prose-invert max-w-none" data-testid="agb-content">
            {/* Title */}
            <div className="mb-8 pb-6 border-b border-border">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="w-8 h-8 text-primary" />
                <h1 className="text-3xl font-['Oswald'] font-bold tracking-wider uppercase m-0">
                  {agb?.title || 'Allgemeine Geschäftsbedingungen'}
                </h1>
              </div>
              {agb?.updated_at && (
                <p className="text-sm text-muted-foreground mt-2">
                  Stand: {formatDate(agb.updated_at)}
                </p>
              )}
            </div>

            {/* Content */}
            <div className="text-foreground/90">
              {agb?.content ? (
                renderContent(agb.content)
              ) : (
                <p className="text-muted-foreground italic">
                  Die Allgemeinen Geschäftsbedingungen wurden noch nicht hinterlegt.
                </p>
              )}
            </div>
            
            {/* Styles for WYSIWYG HTML content */}
            <style>{`
              .agb-content h2 {
                font-family: 'Oswald', Arial, sans-serif;
                font-size: 1.5em;
                font-weight: 700;
                margin: 1.5em 0 0.75em 0;
                text-transform: uppercase;
                letter-spacing: 0.02em;
                color: hsl(var(--foreground));
              }
              .agb-content h3 {
                font-family: 'Oswald', Arial, sans-serif;
                font-size: 1.25em;
                font-weight: 600;
                margin: 1.25em 0 0.5em 0;
                color: hsl(var(--foreground));
              }
              .agb-content p {
                margin: 0 0 1em 0;
                line-height: 1.7;
              }
              .agb-content ul, .agb-content ol {
                padding-left: 1.5em;
                margin: 0 0 1em 0;
              }
              .agb-content ul {
                list-style-type: disc;
              }
              .agb-content ol {
                list-style-type: decimal;
              }
              .agb-content li {
                margin: 0.25em 0;
                line-height: 1.6;
              }
              .agb-content a {
                color: hsl(var(--primary));
                text-decoration: underline;
              }
              .agb-content strong {
                font-weight: 700;
              }
              .agb-content em {
                font-style: italic;
              }
              .agb-content hr {
                border: none;
                border-top: 2px solid hsl(var(--border));
                margin: 1.5em 0;
              }
            `}</style>
          </article>
        )}
      </main>

      <PublicFooter />
    </div>
  );
};

export default AGBPage;
